import theme from '../../../../../themes/airbo';

// Constants
export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const ITEMS_PER_PAGE = 10;

// Campaign status constants
export const STATUS_TYPES = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  DRAFT: 'draft',
  COMPLETED: 'completed',
  SCHEDULED: 'scheduled',
};

// Campaign status configuration
// Note: "completed" status is treated as "active" (live)
export const STATUS_CONFIG = {
  active: {
    label: 'Live',
    color: theme.colors.success.default,
  },
  completed: {
    label: 'Live',
    color: theme.colors.success.default,
  },
  draft: {
    label: 'Draft',
    color: 'var(--orange-9)',
  },
  archived: {
    label: 'Archived',
    color: theme.colors.purple,
  },
  scheduled: {
    label: 'Scheduled',
    color: 'var(--blue-8)',
  },
};

// Legacy DOT_COLORS for backward compatibility
export const DOT_COLORS = {
  active: STATUS_CONFIG.active.color,
  archived: STATUS_CONFIG.archived.color,
  draft: STATUS_CONFIG.draft.color,
  completed: STATUS_CONFIG.active.color, // completed treated as active
  scheduled: STATUS_CONFIG.scheduled.color,
};

// Broadcast status constants
export const BROADCAST_STATUS_TYPES = {
  SCHEDULED: 'scheduled',
  SENT: 'sent',
  ERROR: 'error',
  DRAFT: 'draft',
};

// Broadcast status configuration
export const BROADCAST_STATUS_CONFIG = {
  scheduled: {
    label: 'Scheduled',
    color: 'var(--blue-8)',
  },
  sent: {
    label: 'Sent',
    color: 'var(--green-9)',
  },
  error: {
    label: 'Error',
    color: 'var(--red-9)',
  },
  draft: {
    label: 'Draft',
    color: 'var(--orange-9)',
  },
};

// Broadcast DOT_COLORS
export const BROADCAST_DOT_COLORS = {
  scheduled: BROADCAST_STATUS_CONFIG.scheduled.color,
  sent: BROADCAST_STATUS_CONFIG.sent.color,
  error: BROADCAST_STATUS_CONFIG.error.color,
  draft: BROADCAST_STATUS_CONFIG.draft.color,
};

/**
 * Generates an array of 12 months for a given year
 * @param {number} year - The year to generate months for
 * @returns {Array} Array of month objects with month name, year, key, and monthIndex
 */
export function get12MonthRange(year) {
  const months = [];
  for (let i = 0; i < 12; i++) {
    months.push({
      month: MONTHS[i],
      year,
      key: `${MONTHS[i]}-${year}`,
      monthIndex: i,
    });
  }
  return months;
}

/**
 * Formats a date string to a readable format
 * @param {string|Date} dateStr - The date to format
 * @returns {string} Formatted date string or empty string if invalid
 */
export function formatDate(dateStr) {
  if (!dateStr) return '';

  try {
    // Handle different date formats
    let date;
    if (typeof dateStr === 'string') {
      if (dateStr.includes('-')) {
        // YYYY-MM-DD format
        const [year, month, day] = dateStr.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        // Try parsing as ISO string or other format
        date = new Date(dateStr);
      }
    } else if (dateStr instanceof Date) {
      date = dateStr;
    } else {
      return '';
    }

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * Gets the number of days in a given month
 * @param {number} year - The year
 * @param {number} monthIndex - The month index (0-11)
 * @returns {number} Number of days in the month
 */
export function getDaysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/**
 * Gets the day of week for the first day of a month (0 = Sunday, 6 = Saturday)
 * @param {number} year - The year
 * @param {number} monthIndex - The month index (0-11)
 * @returns {number} Day of week (0-6)
 */
export function getFirstDayOfMonth(year, monthIndex) {
  return new Date(year, monthIndex, 1).getDay();
}

/**
 * Parses a date string to get day, month, year
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {object|null} Object with day, month, year or null if invalid
 */
export function parseDateString(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;

  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;

  const [year, month, day] = parts.map(Number);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day))
    return null;

  return { year, month: month - 1, day }; // month is 0-indexed
}

/**
 * Gets campaigns that start or end on a specific day
 * @param {number} day - The day of month
 * @param {number} monthIndex - The month index (0-11)
 * @param {number} year - The year
 * @param {Array} campaigns - Array of campaigns
 * @returns {Array} Campaigns with isStart/isEnd flags
 */
export function getCampaignsForDay(day, monthIndex, year, campaigns) {
  const results = [];

  campaigns.forEach(campaign => {
    // Drafts with posts: show one entry per post on this day, each with the post's status
    if (campaign.status === 'draft' && campaign.earliestPostDate) {
      (campaign.postDetails || []).forEach(post => {
        const postDate = parseDateString(post.date);
        if (
          postDate &&
          postDate.day === day &&
          postDate.month === monthIndex &&
          postDate.year === year
        ) {
          results.push({ ...campaign, postStatus: post.status });
        }
      });
      return;
    }

    const startDate = parseDateString(campaign.startDate);
    const matchesStart =
      startDate &&
      startDate.day === day &&
      startDate.month === monthIndex &&
      startDate.year === year;

    const matchesPost = (campaign.postDates || []).some(dateStr => {
      const postDate = parseDateString(dateStr);
      return (
        postDate &&
        postDate.day === day &&
        postDate.month === monthIndex &&
        postDate.year === year
      );
    });

    if (matchesStart || matchesPost) {
      results.push(campaign);
    }
  });

  return results;
}

/**
 * Gets the effective display status for a campaign (draft with scheduled posts shows as scheduled)
 * @param {object} campaign - Campaign object
 * @returns {string} Effective status for display
 */
export function getEffectiveDisplayStatus(campaign, monthIndex, year) {
  // When a specific postStatus is set (from getCampaignsForDay), use it directly
  if (campaign.postStatus) {
    if (campaign.postStatus === 'live') return 'active';
    if (campaign.postStatus === 'scheduled') return 'scheduled';
    return 'draft';
  }

  // When month context is provided, use the status of the earliest post in that month
  if (monthIndex !== undefined && year !== undefined) {
    const monthPosts = (campaign.postDetails || []).filter(post => {
      const parsed = parseDateString(post.date);
      return parsed && parsed.month === monthIndex && parsed.year === year;
    });

    if (monthPosts.length > 0) {
      const statuses = monthPosts.map(p => p.status);
      if (statuses.includes('live')) return 'active';
      if (statuses.includes('scheduled')) return 'scheduled';
      return 'draft';
    }
  }

  if (campaign.status === 'draft') {
    if (campaign.hasLivePost) return 'active';
    if (campaign.hasScheduledPost) return 'scheduled';
  }
  return campaign.status;
}

/**
 * Gets the effective display date for a campaign (draft with posts shows earliest post date)
 * @param {object} campaign - Campaign object
 * @returns {string} Effective date string for display
 */
export function getEffectiveDisplayDate(campaign, monthIndex, year) {
  if (monthIndex !== undefined && year !== undefined) {
    const monthPostDates = (campaign.postDates || []).filter(dateStr => {
      const parsed = parseDateString(dateStr);
      return parsed && parsed.month === monthIndex && parsed.year === year;
    });

    if (monthPostDates.length > 0) {
      monthPostDates.sort();
      return monthPostDates[0];
    }
  }

  if (campaign.earliestLivePostDate) return campaign.earliestLivePostDate;
  if (campaign.status === 'draft' && campaign.earliestPostDate)
    return campaign.earliestPostDate;
  return campaign.startDate;
}

/**
 * Gets broadcasts for a specific day
 * @param {number} day - The day of month
 * @param {number} monthIndex - The month index (0-11)
 * @param {number} year - The year
 * @param {Array} broadcasts - Array of broadcasts
 * @returns {Array} Broadcasts on that day
 */
export function getBroadcastsForDay(day, monthIndex, year, broadcasts) {
  return broadcasts.filter(broadcast => {
    const broadcastDate = parseDateString(broadcast.displayDate);
    return (
      broadcastDate &&
      broadcastDate.day === day &&
      broadcastDate.month === monthIndex &&
      broadcastDate.year === year
    );
  });
}
